import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { handleBuyerNotification } from '../../util/api';
import { MARKETPLACE_FILTER } from '../../util/catalogueCore';

const RESULT_PAGE_SIZE = 42;

// ================ Action types ================ //
export const SET_INITIAL_STATE = 'app/BuyListingPage/SET_INITIAL_STATE';

export const GET_BUY_LISTINGS_REQUEST = 'app/BuyListingPage/GET_BUY_LISTINGS_REQUEST';
export const GET_BUY_LISTINGS_SUCCESS = 'app/BuyListingPage/GET_BUY_LISTINGS_SUCCESS';
export const GET_BUY_LISTINGS_ERROR = 'app/BuyListingPage/GET_BUY_LISTINGS_ERROR';

export const GET_BUY_LISTINGS_COUNTS_SUCCESS = 'app/BuyListingPage/GET_BUY_LISTINGS_COUNTS_SUCCESS';

export const GET_SUGGESTIONS_REQUEST = 'app/BuyListingPage/GET_SUGGESTIONS_REQUEST';
export const GET_SUGGESTIONS_SUCCESS = 'app/BuyListingPage/GET_SUGGESTIONS_SUCCESS';
export const GET_SUGGESTIONS_ERROR = 'app/BuyListingPage/GET_SUGGESTIONS_ERROR';


export const GET_BUYER_LISTING_ID = 'app/BuyListingPage/GET_BUYER_LISTING_ID';

// ================ Reducer ================ //

const initialState = {
    pagination: null,
    queryParams: null,
    getBuyListingsInProgress: false,
    getBuyListingsError: null,
    buyListings: [],
    // Accurate counts across ALL requests (not just the current page), used by
    // the season filter chips. totalCount is the true total of all requests.
    seasonCounts: {},
    totalCount: 0,
    getSuggestionsInProgress: false,
    getSuggestionsError: null,
    suggestions: [],
    buyerListingId: null,
};

export default function BuyListingPage(state = initialState, action = {}) {
    const { type, payload } = action;
    switch (type) {
        case SET_INITIAL_STATE:
            return { ...initialState };

        case GET_BUY_LISTINGS_REQUEST:
            return {
                ...state,
                queryParams: payload.queryParams,
                getBuyListingsInProgress: true,
                getBuyListingsError: null,
            };

        case GET_BUY_LISTINGS_SUCCESS:
            return {
                ...state,
                buyListings: payload.data,
                pagination: payload.data.meta,
                getBuyListingsInProgress: false,
            };

        case GET_BUY_LISTINGS_ERROR:
            console.error(payload);
            return { ...state, getBuyListingsInProgress: false, getBuyListingsError: payload };

        case GET_BUY_LISTINGS_COUNTS_SUCCESS:
            return {
                ...state,
                seasonCounts: payload.seasonCounts,
                totalCount: payload.totalCount,
            };


        case GET_SUGGESTIONS_REQUEST:
            return {
                ...state,
                getSuggestionsInProgress: true,
                getSuggestionsError: null,
            };

        case GET_SUGGESTIONS_SUCCESS:
            return {
                ...state,
                suggestions: payload.data,
                getSuggestionsInProgress: false,
            };

        case GET_SUGGESTIONS_ERROR:
            console.error(payload);
            return { ...state, getSuggestionsInProgress: false, getSuggestionsError: payload };

        case GET_BUYER_LISTING_ID:
            return { ...state, buyerListingId: payload }

        default:
            return state;
    }
}

export const setInitialState = () => ({
    type: SET_INITIAL_STATE,
});


export const queryBuyListingsRequest = queryParams => ({
    type: GET_BUY_LISTINGS_REQUEST,
    payload: { queryParams },
});

export const queryBuyListingsSuccess = response => ({
    type: GET_BUY_LISTINGS_SUCCESS,
    payload: { data: response.data },
});

export const queryBuyListingsError = e => ({
    type: GET_BUY_LISTINGS_ERROR,
    error: true,
    payload: e,
});


export const queryCurrentUserSuggetionsRequest = () => ({
    type: GET_SUGGESTIONS_REQUEST,
});

export const queryCurrentUserSuggetionsSuccess = response => ({
    type: GET_SUGGESTIONS_SUCCESS,
    payload: { data: response },
});

export const queryCurrentUserSuggetionsError = e => ({
    type: GET_SUGGESTIONS_ERROR,
    error: true,
    payload: e,
});

export const getBuyerListingId = listingId => ({
    type: GET_BUYER_LISTING_ID,
    payload: listingId,
})

export const queryBuyListings = queryParams => (dispatch, getState, sdk) => {
    dispatch(queryBuyListingsRequest(queryParams));

    const { perPage, ...rest } = queryParams;
    const params = { ...rest, perPage };

    return sdk.listings
        .query(params)
        .then(response => {
            // console.log('l-response', response)
            dispatch(queryBuyListingsSuccess(response));
            return response;
        })
        .catch(e => {
            dispatch(queryBuyListingsError(storableError(e)));
            throw e;
        });
};

export const queryBuyListingsCountsSuccess = (seasonCounts, totalCount) => ({
    type: GET_BUY_LISTINGS_COUNTS_SUCCESS,
    payload: { seasonCounts, totalCount },
});

// Count ALL requests (förfrågningar) by tiretype so the season filter chips
// show true totals, not just the current page. The board is small, so we page
// through everything (perPage 100) requesting only the tiretype field.
export const queryBuyListingsCounts = () => async (dispatch, getState, sdk) => {
    const perPage = 100;
    let page = 1;
    let totalPages = 1;
    let totalCount = 0;
    const seasonCounts = {};

    try {
        while (page <= totalPages) {
            const response = await sdk.listings.query({
                page,
                perPage,
                pub_isDeleted: false,
                pub_isHide: false,
                // P25009-410 — ads only; a catalogue row lives on its seller's profile.
                ...MARKETPLACE_FILTER,
                pub_listingType: 'buying',
                'fields.listing': ['publicData.tiretype'],
            });

            const listings = response?.data?.data || [];
            const meta = response?.data?.meta || {};
            totalCount = meta.totalItems != null ? meta.totalItems : totalCount;
            totalPages = meta.totalPages || 1;

            listings.forEach(l => {
                const t = l?.attributes?.publicData?.tiretype;
                if (t) seasonCounts[t] = (seasonCounts[t] || 0) + 1;
            });

            page += 1;
        }

        dispatch(queryBuyListingsCountsSuccess(seasonCounts, totalCount));
    } catch (e) {
        // Counts are non-critical; the page still renders without them.
        console.error('Failed to load förfrågningar counts', e);
    }
};

export const getCurrentUserSuggestions = (params) => async (dispatch, getState, sdk) => {
    const { listingId } = params;
    dispatch(queryCurrentUserSuggetionsRequest());
    const perPage = 100;
    let page = 1;
    let totalPages = 1;
    let allListings = [];

    const variantPrefix = 'listing-card';
    const aspectWidth = 4;
    const aspectHeight = 3;
    const aspectRatio = aspectHeight / aspectWidth;

    try {
        while (page <= totalPages) {
            const response = await sdk.ownListings.query({
                page,
                perPage,
                pub_isDeleted: false,
                pub_isHide: false,
                // P25009-410 — ads only; a catalogue row lives on its seller's profile.
                ...MARKETPLACE_FILTER,
                states: 'published',
                include: ['images'],
                'fields.listing': ['title', 'publicData'],
                'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
                ...createImageVariantConfig(`${variantPrefix}`, 75, aspectRatio),
                ...createImageVariantConfig(`${variantPrefix}-2x`, 400, aspectRatio),
                'limit.images': 1,
            });

            const listings = response?.data?.data || [];
            const included = response?.data?.included || [];

            const imageMap = {};
            included.forEach(item => {
                if (item.type === 'image') {
                    const imageId = item.id.uuid;
                    const variants = item.attributes?.variants || {};
                    const imageURL = variants?.[`${variantPrefix}-2x`]?.url || variants?.[variantPrefix]?.url;
                    if (imageURL) {
                        imageMap[imageId] = imageURL;
                    }
                }
            });

            // Filter and format listings
            const filteredListings = listings
                .filter(listing => listing?.attributes?.publicData?.listingType !== 'admin-adverts' && listing?.attributes?.publicData?.listingType !== 'buying')
                .map(listing => {
                    const id = listing.id.uuid;
                    const title = listing.attributes?.title || '';
                    const keywords = listing.attributes?.publicData.keywords || '';
                    const imageRel = listing.relationships?.images?.data?.[0];
                    const imageId = imageRel?.id?.uuid;
                    const imageUrl = imageMap[imageId] || null;

                    return { id, title, imageUrl,keywords };
                });

            allListings.push(...filteredListings);

            if (page === 1 && response?.data?.meta?.totalPages) {
                totalPages = response.data.meta.totalPages;
            }

            page++;
        }

        // console.log('Filtered Own Listings:', allListings);
        dispatch(queryCurrentUserSuggetionsSuccess(allListings));
        dispatch(getBuyerListingId(listingId));
        return allListings;

    } catch (error) {
        dispatch(queryCurrentUserSuggetionsError(error));
        throw error;
    }
};

export const NotifyBuyer = (params) => async (dispatch, getState, sdk) => {
    handleBuyerNotification(params);
}


export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
    dispatch(setInitialState());
    const queryParams = parse(search);
    // console.log('queryParams',queryParams)
    const page = queryParams.page || 1;

    const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;

    const params = {
     ...queryParams,
            page,
            perPage: RESULT_PAGE_SIZE,
            pub_isDeleted: false,
            pub_isHide: false,
            // P25009-410 — ads only; a catalogue row lives on its seller's profile.
            ...MARKETPLACE_FILTER,
            pub_listingType: 'buying',
            include: 'images',
            'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
            ...createImageVariantConfig(`${variantPrefix}`, 75, aspectRatio),
            ...createImageVariantConfig(`${variantPrefix}-2x`, 400, aspectRatio),
            'limit.images': 1,
    }
    if (queryParams.listingId) {
  params.ids = queryParams.listingId; // Filter to only this listing
}
    await Promise.all([
        dispatch(queryBuyListings((params))),
        // Accurate season-chip counts across all pages (not just this page).
        dispatch(queryBuyListingsCounts()),
    ]);
};